import { MarkGlyph } from "./MarkGlyph";
import { validColor, measurementLabel } from "../shared/mark-presentation";
import { sourceMeters, scaleSource } from "./source-scale";
import { SHEET_CAN_RESIZE } from "./sheet-operations";
import { getLineAlignment } from "./line-alignment";
import { getLineGuide } from "./line-guide-state";
import { traceProps, type Trace } from "../shared/schema";
import { paperOpacity } from "../shared/trace-paper";
import { BaseFrameLikeShapeUtil, ShapeUtil, HTMLContainer, SVGContainer, Polygon2d, Polyline2d, Group2d, Rectangle2d, type HandleDragInfo, Vec } from "./canvas";
import { FileHelpers } from "@tldraw/utils";
import type { Editor } from "./canvas";
import { useValue } from "@tldraw/state-react";
import type { TLShape } from "@tldraw/tlschema";
import { sheetProps, markProps, type Sheet, type Mark } from "../shared/schema";
import {
  distance,
  polygonArea,
  angleDegrees,
  formatLength,
} from "../shared/geometry";

export function sheetFor(editor: Editor, shape?: TLShape): Sheet | undefined {
  if (!shape) return undefined;
  if (shape.type === "sheet") return shape;
  if (shape.parentId.startsWith("shape:"))
    return sheetFor(editor, editor.getShape(shape.parentId as Sheet["id"]));
}
export const markLabel = measurementLabel;
export class SheetUtil extends BaseFrameLikeShapeUtil<Sheet> {
  static override type = "sheet" as const;
  static override props = sheetProps;
  // Paper owns annotations; it must not attract arrow endpoints to its center.
  override canBind() {
    return false;
  }
  override getDefaultProps() {
    return {
      w: 1120,
      h: 792,
      name: "Untitled drawing",
      source: "",
      preview: "",
      page: 1,
      paper: "A3",
      metersPerUnit: 0,
      calibration: "Not calibrated",
      revision: "01",
    };
  }
  // Click paper to select; drag its selected title to move the sheet.
  override onBeforeCreate(shape: Sheet) {
    const names = new Set(
      this.editor
        .getCurrentPageShapes()
        .filter((s) => s.type === "sheet")
        .map((s) => s.props.name),
    );
    if (!names.has(shape.props.name)) return shape;
    const base = shape.props.name.replace(/ \(copy(?: \d+)?\)$/, "");
    let name = `${base} (copy)`;
    for (let i = 2; names.has(name); i++) name = `${base} (copy ${i})`;
    return { ...shape, props: { ...shape.props, name } };
  }
  override getGeometry(shape: Sheet) {
    return new Group2d({
      children: [
        new Rectangle2d({
          width: shape.props.w,
          height: shape.props.h,
          isFilled: false,
        }),
        new Rectangle2d({
          x: 0,
          y: -27,
          width: shape.props.w,
          height: 22,
          isFilled: true,
          isLabel: true,
          excludeFromShapeBounds: true,
        }),
      ],
    });
  }
  override hideSelectionBoundsBg() {
    return true;
  }
  override canReceiveNewChildrenOfType(shape: Sheet, type: TLShape["type"]) {
    return !shape.isLocked && !["sheet", "trace"].includes(type);
  }
  override canResizeChildren() {
    return false;
  }
  override canResize() {
    return SHEET_CAN_RESIZE;
  }
  override onResize(shape: Sheet, info: import("./canvas").ResizeInfo<Sheet>) {
    const resized = super.onResize(shape, info);
    const w = resized.props?.w ?? shape.props.w,
      h = resized.props?.h ?? shape.props.h;
    return {
      ...resized,
      props: { ...resized.props, paper: "Custom" },
      meta: {
        ...shape.meta,
        paperWidthMm: (w * 3) / 8,
        paperHeightMm: (h * 3) / 8,
      },
    };
  }
  override isAspectRatioLocked(shape: Sheet) {
    return !!shape.props.source;
  }
  override hideRotateHandle() {
    return true;
  }
  override shouldClipChild() {
    return false;
  }
  override getClipPath() {
    return undefined;
  }
  override canRemoveChildrenOfType() {
    return false;
  }
  override onDragShapesOut() {}
  override component(shape: Sheet) {
    return (
      <HTMLContainer
        className="drawing-sheet"
        style={{
          width: shape.props.w,
          height: shape.props.h,
          background: validColor(shape.meta.backgroundColor),
        }}
      >
        <div
          className="sheet-caption"
          title="Click to select this sheet, then drag its title to move it and its contents"
        >
          {shape.props.name}
          <span>REV {shape.props.revision}</span>
        </div>
        {shape.props.preview && (
          <img
            src={shape.props.preview}
            alt={shape.props.name}
            draggable={false}
          />
        )}
      </HTMLContainer>
    );
  }
  override async toSvg(shape: Sheet) {
    // SVG images need embedded bytes when rasterized; a relative private URL
    // disappears inside a standalone SVG image document.
    let preview = shape.props.preview;
    if (preview && !preview.startsWith("data:")) {
      const response = await fetch(preview);
      if (!response.ok)
        throw new Error("The plan preview could not be loaded. Try again.");
      preview = await FileHelpers.blobToDataUrl(await response.blob());
    }
    return (
      <g>
        <rect
          width={shape.props.w}
          height={shape.props.h}
          fill={validColor(shape.meta.backgroundColor)}
        />
        {shape.props.preview && (
          <image href={preview} width={shape.props.w} height={shape.props.h} />
        )}
      </g>
    );
  }
  override getIndicatorPath(shape: Sheet) {
    const p = new Path2D();
    p.rect(0, 0, shape.props.w, shape.props.h);
    return p;
  }
}
function MarkView({ shape, editor }: { shape: Mark; editor: Editor }) {
  const meters = useValue(
    "drawing scale",
    () => sourceMeters(editor, scaleSource(editor, shape)),
    [editor, shape.parentId],
  );
  return (
    <SVGContainer style={{ overflow: "visible" }}>
      <MarkGlyph shape={shape} meters={meters} />
    </SVGContainer>
  );
}
export class MarkUtil extends ShapeUtil<Mark> {
  static override type = "mark" as const;
  static override props = markProps;
  override getDefaultProps(): Mark["props"] {
    return {
      kind: "dimension",
      points: [
        { x: 0, y: 0 },
        { x: 100, y: 0 },
      ],
      color: "#d65331",
      weight: 2,
    };
  }
  override canResize() {
    return false;
  }
  override hideRotateHandle() {
    return true;
  }
  override getGeometry(shape: Mark) {
    // Review anchors use these stable point bounds; display labels must not move them.
    const points = shape.props.points.map((p) => new Vec(p.x, p.y));
    return shape.props.kind === "area" && points.length >= 3
      ? new Polygon2d({ points, isFilled: true })
      : new Polyline2d({ points });
  }
  override component(shape: Mark) {
    return <MarkView shape={shape} editor={this.editor} />;
  }
  override toSvg(shape: Mark) {
    return (
      <MarkGlyph
        shape={shape}
        meters={sourceMeters(this.editor, scaleSource(this.editor, shape))}
      />
    );
  }
  override getIndicatorPath(shape: Mark) {
    const path = new Path2D();
    shape.props.points.forEach((p, i) =>
      i ? path.lineTo(p.x, p.y) : path.moveTo(p.x, p.y),
    );
    if (shape.props.kind === "area") path.closePath();
    return path;
  }
  override getHandles(shape: Mark) {
    return shape.props.points.map((p, i) => ({
      id: String(i),
      type: "vertex" as const,
      index: `a${i}` as any,
      x: p.x,
      y: p.y,
      canBind: false,
    }));
  }
  override onHandleDragStart() {
    getLineGuide(this.editor).set(null);
  }
  override onHandleDrag(shape: Mark, info: HandleDragInfo<Mark>) {
    const index = Number(info.handle.id);
    let point = { x: info.handle.x, y: info.handle.y };
    const guide = getLineGuide(this.editor);
    if (
      shape.props.kind === "line" &&
      shape.props.points.length === 2 &&
      (index === 0 || index === 1) &&
      !this.editor.inputs.altKey
    ) {
      const start = shape.props.points[1 - index];
      const alignment = getLineAlignment(
        start,
        point,
        this.editor.getZoomLevel(),
        guide.get()?.axis,
      );
      // Native handle dragging already applies the explicit Shift constraint.
      if (!this.editor.inputs.shiftKey) point = alignment.point;
      const transform = this.editor.getShapePageTransform(shape);
      guide.set(
        alignment.axis
          ? {
              start: transform.applyToPoint(start),
              end: transform.applyToPoint(point),
              axis: alignment.axis,
              locked: this.editor.inputs.shiftKey,
            }
          : null,
      );
    } else {
      guide.set(null);
    }
    return {
      ...shape,
      props: {
        ...shape.props,
        points: shape.props.points.map((p, i) => i === index ? point : p),
      },
    };
  }
  override onHandleDragEnd() {
    getLineGuide(this.editor).set(null);
  }
  override onHandleDragCancel() {
    getLineGuide(this.editor).set(null);
  }
}
export class TraceUtil extends BaseFrameLikeShapeUtil<Trace> {
  static override type = "trace" as const;
  static override props = traceProps;
  override canBind() {
    return false;
  }
  override getDefaultProps() {
    return { w: 320, h: 240, name: "Trace", hidden: false };
  }
  override canReceiveNewChildrenOfType(shape: Trace, type: TLShape["type"]) {
    return (
      !shape.isLocked &&
      !shape.props.hidden &&
      !["sheet", "trace"].includes(type)
    );
  }
  override canRemoveChildrenOfType() {
    return false;
  }
  override onDragShapesOut() {}
  override canResize(shape: Trace) {
    return shape.parentId.startsWith("page:");
  }
  override canResizeChildren() {
    return false;
  }
  override hideRotateHandle() {
    return true;
  }
  override isAspectRatioLocked() {
    return false;
  }
  override shouldClipChild() {
    return false;
  }
  override getClipPath() {
    return undefined;
  }
  override onDragShapesIn() {} // Laying paper over objects must never steal them.
  override getGeometry(shape: Trace) {
    return new Group2d({
      children: [
        new Rectangle2d({
          width: shape.props.w,
          height: shape.props.h,
          isFilled: false,
        }),
        new Rectangle2d({
          x: 0,
          y: -30,
          width: shape.props.w,
          height: 26,
          isFilled: true,
          isLabel: true,
          excludeFromShapeBounds: true,
        }),
      ],
    });
  }
  override hideSelectionBoundsBg() {
    return true;
  }
  override component(shape: Trace) {
    return (
      <HTMLContainer
        className="trace-sheet"
        style={{
          width: shape.props.w,
          height: shape.props.h,
          backgroundColor: `rgba(255, 255, 255, ${paperOpacity(shape)})`,
        }}
      >
        <span>
          {shape.props.name}
          <small>Drag the edge to move</small>
        </span>
      </HTMLContainer>
    );
  }
  override toSvg() {
    return <g />;
  }
  override getIndicatorPath(shape: Trace) {
    const p = new Path2D();
    p.rect(0, 0, shape.props.w, shape.props.h);
    return p;
  }
}
